/** @file Closure of a removal: the entity and everything that cannot exist without it (reads, never writes). */
import type { Doc } from './schema';
import type { EntityKind } from './entityKinds';

type Found = [kind: EntityKind, id: string];
type Removed = Map<string, EntityKind>;

const tag = (kind: EntityKind) => (id: string): Found => [kind, id];
const keysWhere = <T>(table: Record<string, T>, pick: (value: T) => boolean): string[] =>
  Object.entries(table).filter(([, value]) => pick(value)).map(([key]) => key);
const none = (): Found[] => [];

/** Entities that depend directly on one removed entity (orphaned attachments are found after the closure). */
const DEPENDENTS: Record<EntityKind, (doc: Doc, id: string) => Found[]> = {
  bone: (doc, id) => [
    ...keysWhere(doc.bones, (b) => b.parent === id).map(tag('bone')),
    ...keysWhere(doc.slots, (s) => s.bone === id).map(tag('slot')),
    ...keysWhere(doc.constraints, (c) => c.target === id || c.bones.includes(id)).map(tag('ik')),
    ...keysWhere(doc.sockets, (s) => s.parentBone === id || s.bone === id).map(tag('socket')),
  ],
  socket: (doc, id) => {
    const socket = doc.sockets[id];
    return socket ? [socket.bone, socket.grip2Bone].filter((b): b is string => b !== undefined).map(tag('bone')) : [];
  },
  image: (doc, id) => [
    ...keysWhere(doc.attachments, (a) => a.image === id).map(tag('attachment')),
    ...keysWhere(doc.references, (r) => r.image === id).map(tag('reference')),
  ],
  slot: none, attachment: none, skin: none, ik: none, event: none, animation: none, reference: none,
};

/** Slots on a removed socket's generated bones survive: removeEntity moves them to the socket's parent bone. */
function keptSlots(doc: Doc, kind: EntityKind, id: string): ReadonlySet<string> {
  const socket = kind === 'socket' ? doc.sockets[id] : undefined;
  if (!socket) return new Set();
  const generated = new Set([socket.bone, socket.grip2Bone]);
  return new Set(keysWhere(doc.slots, (s) => generated.has(s.bone)));
}

function closure(doc: Doc, kind: EntityKind, id: string): Removed {
  const removed: Removed = new Map([[id, kind]]);
  const kept = keptSlots(doc, kind, id);
  const queue: Found[] = [[kind, id]];
  for (let next = queue.shift(); next; next = queue.shift()) {
    for (const [depKind, dep] of DEPENDENTS[next[0]](doc, next[1])) {
      if (removed.has(dep) || kept.has(dep)) continue;
      removed.set(dep, depKind);
      queue.push([depKind, dep]);
    }
  }
  return removed;
}

/** Attachments that had skin entries and lose every one of them (their slot or skin is removed). */
function orphanedAttachments(doc: Doc, removed: Removed): string[] {
  const kept = new Set<string>();
  const lost = new Set<string>();
  for (const [skinId, skin] of Object.entries(doc.skins)) {
    for (const e of skin.entries) (removed.has(skinId) || removed.has(e.slot) ? lost : kept).add(e.attachment);
  }
  return [...lost].filter((att) => !kept.has(att) && !removed.has(att));
}

/** Skinned meshes that would keep a vertex with no influence once the removed bones are stripped. */
function emptiedMeshes(doc: Doc, removed: Removed): string[] {
  const empties = (vertex: [string, number][]): boolean => vertex.length > 0 && vertex.every(([bone]) => removed.has(bone));
  return keysWhere(doc.attachments, (a) => a.type === 'mesh' && a.geometry.kind === 'skinned' && a.geometry.weights.some(empties));
}

/** Every entity removing `kind`/`id` takes with it, in discovery order (the entity first); never mutates `doc`. */
export function collectRemovals(doc: Doc, kind: EntityKind, id: string): Map<string, EntityKind> {
  const removed = closure(doc, kind, id);
  for (const att of [...orphanedAttachments(doc, removed), ...emptiedMeshes(doc, removed)]) removed.set(att, 'attachment');
  return removed;
}
